import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import AuthenticationContainer from 'app/login/containers/AuthenticationContainer';
import ResolveSerieDisplay from './ResolveSerieDisplay';
import { SKILL_PAGE_URL } from 'app/Routes';
import { Redirect, useLocation } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import { selectIsExamMode, selectSerieToResolve } from 'app/redux/selectors/Question/serie.selector';
import { selectSerieHistoryUpdate } from 'app/redux/selectors/Question/history.selector';
import { selectLoading } from 'app/redux/selectors/Loading/loading.selector';
import { postSerieHistory, resetSerieHistoryId } from 'app/redux/actions/Question/History/history.actions';
import {
    getRandomSerieByChapterId,
    GET_RANDOM_SERIE_BY_CHAPTER_ID,
    resetSerieToResolve
} from 'app/redux/actions/Question/Serie/SerieGet/serieGet.actions';
import { notificationError } from 'app/redux/actions/Notification/notifications.actions';
import { GET_SERIE_CHAPTER_ERROR } from 'app/Snackbar/NotificationMessages';
import { mapDurationToObject } from '../../utils/date.functions';
import { postInteractiveQuizParamsApi } from 'app/api/interactiveQuizApi';
import { getSerieBySerieHistoryIdForNewLeader } from 'app/redux/actions/InteractiveQuiz/interactiveQuizRoom.actions';
import { selectSerieParamsRoomByUuid } from 'app/redux/selectors/InteractiveQuiz/interactiveQuizRoom.selector';

function ResolveSerieRequestByChapterId(props) {

    const getCurrentUserId = () => AuthenticationContainer.getCurrentUserId();

    const dispatch = useDispatch();
    const { search } = useLocation();
    const extractParamsUrl = useMemo(() => new URLSearchParams(search), [search]);

    const serieToResolve = useSelector(selectSerieToResolve);
    const serieHistoryUpdate = useSelector(selectSerieHistoryUpdate);
    const isLoading = useSelector(selectLoading)[GET_RANDOM_SERIE_BY_CHAPTER_ID];
    const examMode = useSelector(selectIsExamMode);
    const serieParamsRoomByUuid = useSelector(selectSerieParamsRoomByUuid(props.quizUuidFromLeader));

    useEffect(() => {
        if(props.shouldContinueQuizWithNewLeader) { 
            dispatch(getSerieBySerieHistoryIdForNewLeader(serieHistoryUpdate.id, getCurrentUserId(), serieParamsRoomByUuid.questionsId));
        } else if(props.maxDuration === '--:--') {
            const params = {
                withChildren: props.serieParams.withChildren,
                isRandomSerie: props.serieParams.isRandomSerie,
                nbQuestion: props.serieParams.nbQuestions,
                language: props.serieParams.language,
                userId: getCurrentUserId()
            };
            dispatch(getRandomSerieByChapterId(props.serieParams.chapterId, params));
        } else {
            const params = {
                withChildren: props.serieParams?.withChildren,
                isRandomSerie: props.serieParams?.isRandomSerie,
                nbQuestion: props.serieParams?.nbQuestions,
                language: props.serieParams?.language,
                userId: getCurrentUserId(),
                maxDuration: props.serieParams?.maxDuration
            };
            dispatch(getRandomSerieByChapterId(props.serieParams?.chapterId, params));
        }

        return () => {
            dispatch(resetSerieToResolve());
            dispatch(resetSerieHistoryId());
        };
    }, [props.chapterId]);

    useEffect(() => {
        if(props.shouldContinueQuizWithNewLeader) { return; }
        if(serieToResolve?.questionAskedList?.length) {
            if(props.quizUuidFromLeader) {
                postInteractiveQuizParamsApi(
                    props.quizUuidFromLeader, props.serieParams.skillId, props.serieParams.chapterId,
                    serieToResolve.questionAskedList.map((question) => question.id)
                );
            } else {
                const params = {
                    userId: getCurrentUserId(),
                    skillId: props.serieParams.skillId,
                    chapterId: props.serieParams.chapterId,
                    taskId: Number(extractParamsUrl.get('taskId')),
                    questionsId: serieToResolve.questionAskedList.map((question) => question.id),
                    examMode
                };
                dispatch(postSerieHistory(params));
            }
        }
    }, [serieToResolve]);

    const redirectLink = props.serieParams?.skillName 
        ? `${SKILL_PAGE_URL}/${props.serieParams.skillName}`
        : SKILL_PAGE_URL;
    
    const shouldRenderResolveSerieDisplay = 
        (props.shouldContinueQuizWithNewLeader && serieToResolve && Object.keys(serieToResolve).length > 0) || 
        (!props.shouldContinueQuizWithNewLeader && (
            serieHistoryUpdate || (props.quizUuidFromLeader && serieToResolve && Object.keys(serieToResolve).length > 0)
        ));
    
    return (
        <div data-testid="resolve-serie-request" style={{ height: '100%' }}>
            {shouldRenderResolveSerieDisplay &&
                <ResolveSerieDisplay
                    chapterId={props.serieParams.chapterId}
                    questionsList={serieToResolve.questionAskedList}
                    nbTotalQuestions={serieToResolve.questionAskedList.length}
                    serieHistoryUpdate={serieHistoryUpdate}
                    redirect={redirectLink}
                    quizUuidFromLeader={props.quizUuidFromLeader}
                    usersInRoom={props.usersInRoom}
                    leaderUser={props.leaderUser}
                    maxDuration={mapDurationToObject(serieToResolve.maxDuration)}
                />
            }
            {!isLoading && serieToResolve?.questionAskedList && !serieToResolve.questionAskedList.length &&
                dispatch(notificationError(GET_SERIE_CHAPTER_ERROR)) &&
                <Redirect to={redirectLink} />
            }
        </div >
    );
}

ResolveSerieRequestByChapterId.propTypes = {
    serieParams: PropTypes.shape({
        skillName: PropTypes.string,
        skillId: PropTypes.number.isRequired,
        chapterId: PropTypes.number.isRequired,
        nbQuestions: PropTypes.string,
        withChildren: PropTypes.bool,
        isRandomSerie: PropTypes.bool,
        language: PropTypes.string,
        maxDuration: PropTypes.number
    }),
    quizUuidFromLeader: PropTypes.string,
    shouldContinueQuizWithNewLeader: PropTypes.bool,
    usersInRoom: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        imageUrl: PropTypes.string
    })),
    leaderUser: PropTypes.shape({
        id: PropTypes.number,
        imageUrl: PropTypes.string
    }),
    maxDuration: PropTypes.string,
    chapterId: PropTypes.number
};

export default ResolveSerieRequestByChapterId;
